import { AutoboardVehicle } from '../../../src/app/@shared-types/vehicle';
import { EFuelType } from '../../../src/app/@shared-types/vehicle/enums';
import { VehicleCheckResult } from '../../../src/app/@shared-types/vehicle/check';

/** DB id of the Pkw-EnVKV energy label template (`pdf_template.id`). */
export const CO2_LABEL_TEMPLATE_ID = 'co2-label';

/** Strict completeness check for the Pkw-EnVKV energy label.
 *
 * Unlike the relaxed advertising check (`vehicle.onlineCheck`), the label itself
 * needs the full WLTP breakdown (all driving phases). This mirrors the render
 * requirement in awi-app's `canDisplayCo2Label` so the user sees missing values
 * before trying to create the document. */
export const checkCo2LabelData = (vehicle: AutoboardVehicle): VehicleCheckResult => {
  const emissions = vehicle.vehicleData.emissions;
  if (!emissions) {
    return {
      error: true,
      errorCodes: ['Für das Pkw-EnVKV-Label wurden noch keine Verbrauchsangaben erfasst.'],
    };
  }

  const {
    fuelConsumption, energyConsumption, co2, electricReach,
  } = emissions;
  const fuelType = vehicle.modelData?.engine.fuel.type;
  const errors: string[] = [];

  if (fuelType === EFuelType.Electricity) {
    if (!energyConsumption.combined) errors.push('Stromverbrauch kombiniert');
    if (!energyConsumption.low) errors.push('Stromverbrauch Innenstadt (Niedrig)');
    if (!energyConsumption.medium) errors.push('Stromverbrauch Stadtrand (Medium)');
    if (!energyConsumption.high) errors.push('Stromverbrauch Landstraße (Hoch)');
    if (!energyConsumption.highest) errors.push('Stromverbrauch Autobahn (Höchstwert)');
    if (!electricReach.aer) errors.push('Elektrische Reichweite (AER)');
  } else if (
    fuelType === EFuelType.HybridDieselPlugin
    || fuelType === EFuelType.HybridPetrolPlugin
  ) {
    if (!fuelConsumption.combinedWeighted) errors.push('Kraftstoffverbrauch kombiniert, gewichtet');
    if (!fuelConsumption.combined) errors.push('Kraftstoffverbrauch kombiniert (entladene Batterie)');
    if (!fuelConsumption.low) errors.push('Kraftstoffverbrauch Innenstadt (entladene Batterie)');
    if (!fuelConsumption.medium) errors.push('Kraftstoffverbrauch Stadtrand (entladene Batterie)');
    if (!fuelConsumption.high) errors.push('Kraftstoffverbrauch Landstraße (entladene Batterie)');
    if (!fuelConsumption.highest) errors.push('Kraftstoffverbrauch Autobahn (entladene Batterie)');
    if (!energyConsumption.combinedWeighted) errors.push('Stromverbrauch kombiniert, gewichtet');
    if (!energyConsumption.combined) errors.push('Stromverbrauch kombiniert (rein elektrisch)');
    if (!energyConsumption.low) errors.push('Stromverbrauch Innenstadt (rein elektrisch)');
    if (!energyConsumption.medium) errors.push('Stromverbrauch Stadtrand (rein elektrisch)');
    if (!energyConsumption.high) errors.push('Stromverbrauch Landstraße (rein elektrisch)');
    if (!energyConsumption.highest) errors.push('Stromverbrauch Autobahn (rein elektrisch)');
    if (!electricReach.eaer) errors.push('Elektrische Reichweite (EAER)');
    if (!co2.combinedWeighted) errors.push('CO₂-Emissionen kombiniert, gewichtet');
    if (!co2.combinedFuelOnly) errors.push('CO₂-Emissionen bei entladener Batterie');
  } else {
    if (!fuelConsumption.combined) errors.push('Kraftstoffverbrauch kombiniert');
    if (!fuelConsumption.low) errors.push('Kraftstoffverbrauch Innenstadt (Niedrig)');
    if (!fuelConsumption.medium) errors.push('Kraftstoffverbrauch Stadtrand (Medium)');
    if (!fuelConsumption.high) errors.push('Kraftstoffverbrauch Landstraße (Hoch)');
    if (!fuelConsumption.highest) errors.push('Kraftstoffverbrauch Autobahn (Höchstwert)');
    if (!co2.combined) errors.push('CO₂-Emissionen kombiniert');
  }

  if (!errors.length) return { error: false };

  return {
    error: true,
    errorCodes: ['Für das Pkw-EnVKV-Label fehlen noch folgende Angaben:', ...errors],
  };
};
